import { useEffect, useRef, useState } from 'react';

import '../styles/use-expandable.css';

import { useWindowSize } from './useWindowSize';

interface UseExpandable {
  /**
   * Collapsible content
   */
  content: React.ReactNode;
  /**
   * Indicates whether content is initially expanded
   */
  isExpanded?: boolean;
  /**
   * Indicates whether content should collapse on blur with delay in ms
   */
  collapseOnBlur?: true | { delay: number };
  /**
   * Indicates whether content should collapse on click interaction
   */
  collapseOnContentClick?: boolean;
  /**
   * Indicates whether content height should be recalculated on resize
   */
  recalculateOnResize?: boolean;
}

export const useExpandable = <T extends HTMLElement>(
  {
    isExpanded = false,
    collapseOnBlur,
    collapseOnContentClick = false,
    recalculateOnResize = true,
    content,
  }: UseExpandable,
) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const [expandableDiv, setExpandableDiv] = useState<HTMLDivElement>();
  const ref = useRef<T>(null);
  const { width } = useWindowSize();

  const delay = typeof collapseOnBlur === 'boolean' ? 0 : collapseOnBlur?.delay;

  useEffect(() => {
    if (ref.current) {
      const expandableDiv = ref.current.querySelector('.expandable-container') as HTMLDivElement;
      setExpandableDiv(expandableDiv);
    }
  }, [ref])

  useEffect(() => {
    if (isExpanded && expandableDiv) {
      onOpen(expandableDiv.firstElementChild?.scrollHeight);
    }
  }, [expandableDiv]);

  useEffect(() => {
    const onCollapse = (e: MouseEvent) => {
      const el = ref.current;
      if (el && !el.contains(e.target as Node) && el.classList.contains('expandable-expanded')) {
        el.classList.remove('expandable-expanded');

        const expandableDiv = el.querySelector('.expandable-container') as HTMLDivElement;
        setTimeout(() => (expandableDiv.style.height = '0px'), delay);
      }
    };

    if (collapseOnBlur) {
      document.addEventListener('click', onCollapse);
    }

    return () => document.removeEventListener('click', onCollapse);
  }, []);

  useEffect(() => {
    if (recalculateOnResize) {
      if (expandableDiv && expandableDiv.clientHeight) {
        expandableDiv.setAttribute(
          'style',
          `height: ${expandableDiv.firstElementChild?.scrollHeight}px;`,
        );
      }
    }
  }, [width, content]);

  const onOpen = (customHeight?: number) => {
    if (ref.current && expandableDiv) {
      const expandedHeight = customHeight || expandableDiv.scrollHeight;
      ref.current.classList.add('expandable-expanded');
      expandableDiv.setAttribute('style', `height: ${expandedHeight}px;`);
      setExpanded(true);
    }
  };

  const onClose = () => {
    if (ref.current && expandableDiv) {
      ref.current.classList.remove('expandable-expanded');
      setTimeout(() => (expandableDiv.style.height = '0px'), delay);
      setExpanded(false);
    }
  };

  const onToggle = (customHeight?: number) => {
    if (expandableDiv?.clientHeight) {
      onClose();
    } else {
      onOpen(customHeight || expandableDiv?.scrollHeight);
    }
  };

  return {
    expanded,
    ref,
    expandableDiv,
    onToggle,
    onOpen,
    onClose,
    content: (
      <div
        className="expandable-container"
        onClick={(e) => {
          e.stopPropagation();
          if (collapseOnContentClick) {
            onClose();
          }
        }}
      >
        <div>{content}</div>
      </div>
    ),
  };
};
